import { GOOGLE_OAUTH_CONFIG } from '../config.js';

class GoogleOAuthService {
  constructor() {
    this.clientId = GOOGLE_OAUTH_CONFIG.clientId;
    this.redirectUri = GOOGLE_OAUTH_CONFIG.redirectUri;
    this.scope = GOOGLE_OAUTH_CONFIG.scope;
    this.accessTokenKey = 'google_access_token';
    this.refreshTokenKey = 'google_refresh_token';
    this.isConnectedKey = 'google_connected';
  }

  // Generate OAuth URL
  generateAuthUrl() {
    const params = new URLSearchParams({
      client_id: this.clientId,
      redirect_uri: this.redirectUri,
      scope: this.scope,
      response_type: 'code',
      access_type: 'offline',
      prompt: 'consent',
      state: 'admin_oauth' // Add state for security
    });

    return `https://accounts.google.com/o/oauth2/auth?${params.toString()}`;
  }

  // Handle OAuth callback
  async handleCallback(code) {
    try {
      const response = await fetch('https://oauth2.googleapis.com/token', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams({
          client_id: this.clientId,
          client_secret: import.meta.env.VITE_GOOGLE_CLIENT_SECRET, // You'll need this from env
          code: code,
          grant_type: 'authorization_code',
          redirect_uri: this.redirectUri,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to exchange code for tokens');
      }

      const data = await response.json();
      
      // Store tokens
      this.saveTokens(data.access_token, data.refresh_token);
      
      return data;
    } catch (error) {
      console.error('OAuth callback error:', error);
      throw error;
    }
  }

  // Save tokens to localStorage
  saveTokens(accessToken, refreshToken) {
    localStorage.setItem(this.accessTokenKey, accessToken);
    if (refreshToken) {
      localStorage.setItem(this.refreshTokenKey, refreshToken);
    }
    localStorage.setItem(this.isConnectedKey, 'true');
  }

  // Get stored access token
  getAccessToken() {
    return localStorage.getItem(this.accessTokenKey);
  }

  // Get stored refresh token
  getRefreshToken() {
    return localStorage.getItem(this.refreshTokenKey);
  }

  // Check if connected
  isConnected() {
    return localStorage.getItem(this.isConnectedKey) === 'true' && this.getAccessToken();
  }

  // Disconnect (clear tokens)
  disconnect() {
    localStorage.removeItem(this.accessTokenKey);
    localStorage.removeItem(this.refreshTokenKey);
    localStorage.removeItem(this.isConnectedKey);
  }

  // Get user profile from Google
  async getUserProfile() {
    const accessToken = this.getAccessToken();
    if (!accessToken) {
      throw new Error('No access token available');
    }

    try {
      const response = await fetch('https://www.googleapis.com/oauth2/v2/userinfo', {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch user profile');
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching user profile:', error);
      throw error;
    }
  }

  // Refresh access token
  async refreshAccessToken() {
    const refreshToken = this.getRefreshToken();
    if (!refreshToken) {
      throw new Error('No refresh token available');
    }

    try {
      const response = await fetch('https://oauth2.googleapis.com/token', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams({
          client_id: this.clientId,
          client_secret: import.meta.env.VITE_GOOGLE_CLIENT_SECRET,
          refresh_token: refreshToken,
          grant_type: 'refresh_token',
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to refresh token');
      }

      const data = await response.json();
      this.saveTokens(data.access_token, refreshToken);
      
      return data.access_token;
    } catch (error) {
      console.error('Token refresh error:', error);
      throw error;
    }
  }

  // Get user's calendar list
  async getCalendarList() {
    const accessToken = this.getAccessToken();
    if (!accessToken) {
      throw new Error('No access token available');
    }

    try {
      const response = await fetch('https://www.googleapis.com/calendar/v3/users/me/calendarList', {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });

      if (!response.ok) {
        if (response.status === 401) {
          // Try to refresh token
          await this.refreshAccessToken();
          return this.getCalendarList(); // Retry with new token
        }
        throw new Error('Failed to fetch calendar list');
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching calendar list:', error);
      throw error;
    }
  }

  // Create a calendar event
  async createCalendarEvent(calendarId = 'primary', eventData) {
    const accessToken = this.getAccessToken();
    if (!accessToken) {
      throw new Error('No access token available');
    }

    try {
      const response = await fetch(`https://www.googleapis.com/calendar/v3/calendars/${calendarId}/events`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(eventData),
      });

      if (!response.ok) {
        if (response.status === 401) {
          // Try to refresh token
          await this.refreshAccessToken();
          return this.createCalendarEvent(calendarId, eventData); // Retry with new token
        }
        throw new Error('Failed to create calendar event');
      }

      return await response.json();
    } catch (error) {
      console.error('Error creating calendar event:', error);
      throw error;
    }
  }

  // Send access token to backend for server-side operations
  async sendTokenToBackend() {
    const accessToken = this.getAccessToken();
    const refreshToken = this.getRefreshToken();
    
    if (!accessToken) {
      throw new Error('No access token available');
    }

    // Send tokens to your backend so it can make calendar API calls
    // You'll need to implement this endpoint in your backend
    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/api/v1/google/store-tokens`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`, // Your app's auth token
        },
        body: JSON.stringify({
          access_token: accessToken,
          refresh_token: refreshToken,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to send tokens to backend');
      }

      return await response.json();
    } catch (error) {
      console.error('Error sending tokens to backend:', error);
      throw error;
    }
  }
}

export const googleOAuthService = new GoogleOAuthService(); 